import type { TranslationKeys } from '@vocab/core';
import { useTranslations } from '@vocab/react';
import { Box, Text } from 'braid-design-system';
import type React from 'react';
import { useMemo } from 'react';

import translations from './.vocab';
import loadingIcon from './four-pointed-loading-icon.gif';
import { useCareerFeedAIAssistant } from './hooks/useCareerFeedAIAssistant';

import * as styles from './LoadingOverlay.css';

type TranslationKey = TranslationKeys<typeof translations>;

const DotLoader = () => (
  <Box component="span" className={styles.loadingDot}>
    .
  </Box>
);

const DotsLoader = () => (
  <Box aria-hidden component="span" display="inlineBlock">
    <DotLoader />
    <DotLoader />
    <DotLoader />
  </Box>
);

const loadingPhrases: TranslationKey[] = [
  'Great! Finding those roles for you',
  'Good to know',
  'Thanks for sharing',
  'Working on the right matches',
  'That’s helpful',
];
const getRandomLoadingPhrase = (): TranslationKey =>
  loadingPhrases[Math.floor(Math.random() * loadingPhrases.length)];

const getLoadingPhrase = ({
  isLastQuestion,
  isFirstQuestion,
}: {
  isLastQuestion: boolean;
  isFirstQuestion: boolean;
}): TranslationKey => {
  if (isLastQuestion) {
    return "Let me see if I've got this right";
  } else if (isFirstQuestion) {
    return 'Looking at your job matches';
  }
  return getRandomLoadingPhrase();
};

const GenAIChatLoader = () => {
  const { t } = useTranslations(translations);
  const { progress, isLastQuestion } = useCareerFeedAIAssistant();

  const isFirstQuestion = progress === 'initial';

  const loadingPhrase = useMemo(
    () => getLoadingPhrase({ isFirstQuestion, isLastQuestion }),
    [isFirstQuestion, isLastQuestion],
  );

  return (
    <Box
      width="full"
      height="full"
      position="relative"
      className={styles.transparentBackground}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingBottom="medium"
        height="full"
      >
        <Box
          component="img"
          src={loadingIcon}
          display="block"
          style={{ width: '47px' }}
          marginBottom="small"
        />
        <Text>
          <Box component="span" className={styles.loadingText}>
            {t(loadingPhrase)}
          </Box>
          <DotsLoader />
        </Text>
      </Box>
    </Box>
  );
};

export const GenAIChatLoadingOverlay = ({
  children,
  isGenerating,
}: {
  children: React.ReactNode;
  isGenerating: boolean;
}) => (
  <Box height="full" position="relative">
    <Box
      position="absolute"
      width="full"
      height="full"
      zIndex="modal"
      display={isGenerating ? 'block' : 'none'}
    >
      <GenAIChatLoader />
    </Box>
    {children}
  </Box>
);
